import { memo, useEffect, useRef } from "react";
import Highlighter from "react-highlight-words";
import { findChunks } from "../helper";
import { ClauseHighlight } from "./ClauseHighlight";

const ACTIVE_MARK_CLASS = "bg-[#FFD300]";

type DocumentTextReaderProps = {
  textToHighlight: string;
  searchWords: string[];
  activeMark: number;
  highlightedText: string[];
};

export const DocumentTextReader = memo(
  ({ textToHighlight, searchWords, activeMark, highlightedText }: DocumentTextReaderProps) => {
    const readerRef = useRef<HTMLDivElement | null>(null);

    // Selected text is natively draggable; drop it into the document as plain text only
    useEffect(() => {
      const reader = readerRef.current;
      if (!reader) return undefined;

      const handleDragStart = (e: DragEvent) => {
        const selectedText = globalThis.getSelection()?.toString();
        if (!selectedText || !e.dataTransfer) return;

        e.dataTransfer.setData("text/plain", selectedText);
        e.dataTransfer.setData("text/html", "");
      };

      reader.addEventListener("dragstart", handleDragStart);
      return () => reader.removeEventListener("dragstart", handleDragStart);
    }, []);

    return (
      <div className="flex h-full scroll-py-24 flex-col overflow-x-hidden overflow-y-auto px-4">
        <div ref={readerRef} id="reader-text" className="relative z-0 whitespace-pre-line select-text">
          <Highlighter
            searchWords={searchWords}
            activeClassName={ACTIVE_MARK_CLASS}
            activeIndex={activeMark}
            findChunks={findChunks}
            autoEscape={false}
            textToHighlight={textToHighlight}
            highlightTag={SearchTextHighlight}
          />

          <ClauseHighlight highlightedText={highlightedText} textToHighlight={textToHighlight} />
        </div>
      </div>
    );
  }
);

const SearchTextHighlight = memo(({ className, children, highlightIndex, ...props }: any) => {
  const markRef = useRef<HTMLElement | null>(null);
  const isActive = className.includes(ACTIVE_MARK_CLASS);

  useEffect(() => {
    if (isActive) {
      markRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [children, isActive]);

  return (
    <mark key={children} ref={markRef} className={`z-10 ${isActive ? ACTIVE_MARK_CLASS : "bg-[#FFFD38]"}`} {...props}>
      {children}
    </mark>
  );
});
